import { useMutation } from '@apollo/client/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { toaster } from '#components/toaster/toaster';
import type { GetDocumentByPathQuery } from '#graphql/generated';
import { UPSERT_DOCUMENT } from '#graphql/mutations/upsert-document';
import { documentViewHref } from '#utils/document-app-path';

import { refetchInTransition } from './refetch-utils';
import {
  gridV1StableStringForDirtyCompare,
  parseGridV1Document,
  serializeGridV1Document,
} from './view/viewer/grid-v1-serialize';
import {
  createDefaultGridV1DocumentValue,
  type GridV1DocumentValue,
} from './view/viewer/grid-v1-types';
import { createDefaultMarkdownDocumentValue } from './view/viewer/markdown-document-title';
import { createGridV1DocumentFromMarkdown } from './view/viewer/markdown-to-grid-v1';

export type DocumentByPathResult = GetDocumentByPathQuery['documentByPath'];

function getPathFromResult(
  result: DocumentByPathResult,
  fallback: string,
): string {
  return result?.__typename === 'Document'
    ? result.path
    : result?.__typename === 'DocumentNotFound'
      ? (result.requestedPath ?? fallback)
      : fallback;
}

export interface DocumentContextValue {
  document: DocumentByPathResult;
  path: string;
  displayPath: string;
  documentId: string | undefined;
  documentFormat: string | null;
  formatMetadata: unknown;
  canConvertToGrid: boolean;
  isMarkdownDocument: boolean;
  isGridDocument: boolean;
  isNewDocument: boolean;
  /** Muted app header / editor until user edits a new-path placeholder. */
  editorPlaceholderMuted: boolean;
  childrenItems: Array<{ id: string; path: string }>;
  childrenHasMore: boolean;
  markdownValue: string;
  setMarkdownValue: (value: string) => void;
  gridDocumentValue: GridV1DocumentValue;
  setGridDocumentValue: (value: GridV1DocumentValue) => void;
  /** Grid editor registers a flush for pending debounced cell content before save/serialize. */
  registerGridContentFlush: (flush: (() => void) | null) => void;
  lastSavedSerialized: string;
  isDirty: boolean;
  dirtyEditGeneration: number;
  getSerializedDocument: () => string;
  convertToGrid: () => Promise<void>;
  save: (options?: { silent?: boolean }) => Promise<void>;
  saving: boolean;
  saveInFlightRef: RefObject<boolean>;
  revertToSaved: () => void;
  refetch: () => Promise<unknown>;
}

const DocumentContext = createContext<DocumentContextValue | undefined>(
  undefined,
);

export interface DocumentProviderProps {
  document: DocumentByPathResult;
  path: string;
  refetch: () => Promise<unknown>;
  /** Latest server edit epoch ms from this tab's saves; document page subscription drops stale events against this. */
  lastServerEditTimestampRef: RefObject<number>;
  children: ReactNode;
}

export function DocumentProvider({
  document,
  path,
  refetch,
  lastServerEditTimestampRef,
  children,
}: DocumentProviderProps) {
  const navigate = useNavigate();

  const isNewDocument = document?.__typename === 'DocumentNotFound';
  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath = getPathFromResult(document, path);

  const childrenItems = useMemo(
    () =>
      (document?.children?.items as Array<{
        id: string;
        path: string;
      }> | null) ?? [],
    [document?.children?.items],
  );
  const childrenHasMore = useMemo(
    () => document?.children?.pagination?.hasMore ?? false,
    [document?.children?.pagination?.hasMore],
  );

  const documentValue =
    document?.__typename === 'Document' ? document.value : null;
  const documentFormatFromServer =
    document?.__typename === 'Document' && document.format === 'grid-v1'
      ? 'grid-v1'
      : 'markdown-v1';
  const initialFormatMetadata =
    document?.__typename === 'Document'
      ? (document.formatMetadata ?? null)
      : null;
  const [documentFormat, setDocumentFormat] = useState<string | null>(
    documentFormatFromServer,
  );
  const [savedDocumentFormat, setSavedDocumentFormat] = useState<string | null>(
    documentFormatFromServer,
  );
  const isMarkdownDocument = documentFormat === 'markdown-v1';
  const isGridDocument = documentFormat === 'grid-v1';
  const [formatMetadata, setFormatMetadata] = useState<unknown>(
    initialFormatMetadata,
  );
  const [savedFormatMetadata, setSavedFormatMetadata] = useState<unknown>(
    initialFormatMetadata,
  );

  const [editorPlaceholderMuted, setEditorPlaceholderMuted] =
    useState(isNewDocument);
  const initialMarkdownValue =
    documentFormatFromServer === 'markdown-v1'
      ? (documentValue ??
        (isNewDocument ? createDefaultMarkdownDocumentValue() : ''))
      : createDefaultMarkdownDocumentValue();
  const [markdownValue, setMarkdownValue] = useState(initialMarkdownValue);
  const initialGridDocumentValue =
    documentFormatFromServer === 'grid-v1'
      ? parseGridV1Document(documentValue)
      : createDefaultGridV1DocumentValue();
  const [gridDocumentValue, setGridDocumentValueState] = useState(
    () => initialGridDocumentValue,
  );
  const gridDocumentValueRef = useRef(initialGridDocumentValue);
  const gridContentFlushRef = useRef<(() => void) | null>(null);

  const setGridDocumentValue = useCallback((value: GridV1DocumentValue) => {
    gridDocumentValueRef.current = value;
    setGridDocumentValueState(value);
  }, []);

  const registerGridContentFlush = useCallback((flush: (() => void) | null) => {
    gridContentFlushRef.current = flush;
  }, []);

  const [lastSavedSerialized, setLastSavedSerialized] = useState(() => {
    if (documentFormatFromServer === 'grid-v1') {
      return serializeGridV1Document(parseGridV1Document(documentValue));
    }

    if (documentFormatFromServer === 'markdown-v1') {
      return initialMarkdownValue;
    }

    return initialMarkdownValue;
  });
  const [dirtyEditGeneration, setDirtyEditGeneration] = useState(0);
  const prevSerializedForAutoSaveRef = useRef(
    documentFormatFromServer === 'grid-v1'
      ? serializeGridV1Document(parseGridV1Document(documentValue))
      : initialMarkdownValue,
  );

  const [isDirty, setIsDirty] = useState(false);

  const [upsertDocument, { loading: saving }] = useMutation(UPSERT_DOCUMENT);
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    if (documentFormatFromServer === 'grid-v1') {
      setLastSavedSerialized(
        serializeGridV1Document(parseGridV1Document(documentValue)),
      );
      return;
    }

    setLastSavedSerialized(
      documentValue ??
        (isNewDocument ? createDefaultMarkdownDocumentValue() : ''),
    );
  }, [documentFormatFromServer, documentValue, isNewDocument]);

  useEffect(() => {
    setDocumentFormat(documentFormatFromServer);
    setSavedDocumentFormat(documentFormatFromServer);
    setFormatMetadata(initialFormatMetadata);
    setSavedFormatMetadata(initialFormatMetadata);
    if (documentFormatFromServer === 'grid-v1') {
      setGridDocumentValue(parseGridV1Document(documentValue));
    }
  }, [
    documentFormatFromServer,
    document,
    documentValue,
    initialFormatMetadata,
    setGridDocumentValue,
  ]);

  useEffect(() => {
    if (documentFormatFromServer === 'grid-v1') return;

    setMarkdownValue(
      documentValue ??
        (isNewDocument ? createDefaultMarkdownDocumentValue() : ''),
    );
  }, [documentFormatFromServer, documentValue, isNewDocument]);

  const getSerializedDocument = useCallback(() => {
    if (isGridDocument) {
      gridContentFlushRef.current?.();
      return serializeGridV1Document(gridDocumentValueRef.current);
    }

    return markdownValue;
  }, [isGridDocument, markdownValue]);

  const applyAfterSave = useCallback(
    ({ savedValue }: { savedValue: string }) => {
      if (isMarkdownDocument) {
        setMarkdownValue(savedValue);
        setIsDirty(false);
        setLastSavedSerialized(savedValue);
        setSavedDocumentFormat('markdown-v1');
        setDocumentFormat('markdown-v1');
        return;
      }

      if (isGridDocument) {
        const parsed = parseGridV1Document(savedValue);
        setGridDocumentValue(parsed);
        setIsDirty(false);
        setLastSavedSerialized(savedValue);
        setSavedDocumentFormat('grid-v1');
        setDocumentFormat('grid-v1');
        return;
      }
    },
    [isGridDocument, isMarkdownDocument, setGridDocumentValue],
  );

  const convertToGrid = useCallback(async () => {
    if (documentFormat === 'grid-v1') {
      return;
    }

    try {
      setGridDocumentValue(createGridV1DocumentFromMarkdown(markdownValue));
      setFormatMetadata(null);
      setDocumentFormat('grid-v1');
      setIsDirty(true);
      setDirtyEditGeneration((n) => n + 1);
      setEditorPlaceholderMuted(false);
      toaster.create({
        title: 'Converted',
        description: 'Review the grid layout, then save if it looks right.',
        type: 'success',
      });
    } catch (error) {
      console.error('Convert to grid failed', {
        displayPath,
        documentFormat,
        error,
      });
      toaster.create({
        title: 'Conversion failed',
        description:
          error instanceof Error
            ? error.message
            : 'Unexpected content prevented conversion.',
        type: 'error',
      });
    }
  }, [displayPath, documentFormat, markdownValue, setGridDocumentValue]);

  const save = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (saveInFlightRef.current) return;

      saveInFlightRef.current = true;
      try {
        const value = getSerializedDocument();
        const { data } = await upsertDocument({
          variables: {
            input: {
              id: documentId ?? path,
              path: isNewDocument ? path : displayPath,
              value,
              format: documentFormat === 'grid-v1' ? 'grid-v1' : 'markdown-v1',
              formatMetadata: isMarkdownDocument ? formatMetadata : undefined,
            },
          },
        });

        if (!silent) {
          toaster.create({
            title: 'Success',
            description: isNewDocument
              ? 'Document created successfully'
              : 'Document saved successfully',
          });
        }

        const savedValueString = data?.upsertDocument?.document?.value ?? value;
        const nextFormatMetadata =
          data?.upsertDocument?.document?.formatMetadata ?? null;
        setFormatMetadata(nextFormatMetadata);
        setSavedFormatMetadata(nextFormatMetadata);
        const editTs = data?.upsertDocument?.documentMetadata?.editTimestamp;
        if (typeof editTs === 'number') {
          lastServerEditTimestampRef.current = editTs;
        }

        applyAfterSave({ savedValue: savedValueString });
        setSavedDocumentFormat(documentFormat);

        await refetchInTransition(refetch);

        if (isNewDocument) {
          navigate(documentViewHref(path), { replace: true });
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [
      applyAfterSave,
      displayPath,
      documentFormat,
      documentId,
      formatMetadata,
      getSerializedDocument,
      isMarkdownDocument,
      isNewDocument,
      navigate,
      path,
      refetch,
      lastServerEditTimestampRef,
      upsertDocument,
    ],
  );

  const revertToSaved = useCallback(() => {
    if (savedDocumentFormat === 'markdown-v1') {
      setDocumentFormat('markdown-v1');
      setFormatMetadata(savedFormatMetadata);
      setMarkdownValue(lastSavedSerialized);
      setIsDirty(false);
      return;
    }

    if (savedDocumentFormat === 'grid-v1') {
      setDocumentFormat('grid-v1');
      setFormatMetadata(savedFormatMetadata);
      setGridDocumentValue(parseGridV1Document(lastSavedSerialized));
      setIsDirty(false);
      return;
    }

    setDocumentFormat('markdown-v1');
    setFormatMetadata(savedFormatMetadata);
    setMarkdownValue(lastSavedSerialized);
    setIsDirty(false);
  }, [
    lastSavedSerialized,
    savedDocumentFormat,
    savedFormatMetadata,
    setGridDocumentValue,
  ]);

  useEffect(() => {
    const checkForChanges = () => {
      if (isMarkdownDocument) {
        const dirty = markdownValue !== lastSavedSerialized;
        setIsDirty(dirty);
        if (markdownValue !== prevSerializedForAutoSaveRef.current) {
          prevSerializedForAutoSaveRef.current = markdownValue;
          if (dirty) {
            setDirtyEditGeneration((n) => n + 1);
          }
        }

        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
        return;
      }

      if (isGridDocument) {
        const currentStable = gridV1StableStringForDirtyCompare(
          gridDocumentValueRef.current,
        );
        const dirty = currentStable !== lastSavedSerialized;
        setIsDirty(dirty);
        if (currentStable !== prevSerializedForAutoSaveRef.current) {
          prevSerializedForAutoSaveRef.current = currentStable;
          if (dirty) {
            setDirtyEditGeneration((n) => n + 1);
          }
        }

        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
        return;
      }
    };

    const interval = setInterval(() => void checkForChanges(), 500);
    return () => {
      clearInterval(interval);
    };
  }, [
    gridDocumentValue,
    isGridDocument,
    isMarkdownDocument,
    lastSavedSerialized,
    markdownValue,
    setGridDocumentValue,
  ]);

  const value = useMemo<DocumentContextValue>(
    () => ({
      document,
      path,
      displayPath,
      documentId,
      documentFormat,
      formatMetadata,
      canConvertToGrid: documentFormat === 'markdown-v1',
      isMarkdownDocument,
      isGridDocument,
      isNewDocument,
      editorPlaceholderMuted,
      childrenItems,
      childrenHasMore,
      markdownValue,
      setMarkdownValue,
      gridDocumentValue,
      setGridDocumentValue,
      registerGridContentFlush,
      lastSavedSerialized,
      isDirty,
      dirtyEditGeneration,
      getSerializedDocument,
      convertToGrid,
      save,
      saving,
      saveInFlightRef,
      revertToSaved,
      refetch,
    }),
    [
      childrenHasMore,
      childrenItems,
      convertToGrid,
      documentFormat,
      dirtyEditGeneration,
      displayPath,
      document,
      documentId,
      editorPlaceholderMuted,
      formatMetadata,
      getSerializedDocument,
      gridDocumentValue,
      registerGridContentFlush,
      isDirty,
      isGridDocument,
      isMarkdownDocument,
      isNewDocument,
      lastSavedSerialized,
      markdownValue,
      path,
      refetch,
      revertToSaved,
      save,
      saving,
      setGridDocumentValue,
    ],
  );

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  );
}

export function useDocumentContext(): DocumentContextValue {
  const ctx = useContext(DocumentContext);
  if (!ctx) {
    throw new Error('useDocumentContext must be used within DocumentProvider');
  }

  return ctx;
}
